import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import updateServiceProfiles from 'API/ServiceProfiles/updateServiceProfiles';
import createServiceProfile from 'API/ServiceProfiles/createServiceProfile';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import { validationSchemaTPM } from './schema';
import FormBase from '@/components/forms/new/FormBase';
// import TPMNameField from './formFields/TPMNameField';
import TPMOperatorsField from './formFields/TPMOperatorsField';
import TPMCallingProfilesField from './formFields/TPMCallingProfilesField';
import { axios } from 'services/axios';
import { multiSelectDataMapper, query, toSentenceCase } from 'utils/utils';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useFieldValidation from 'hooks/useFieldValidation';
import { useQuery } from 'hooks/useCustomQuery';
import ServiceProfileVisibleScope from './formFields/ServiceProfileVisibleScope';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from './serviceProfileName';

const TeamsPhoneMobile = ({
    mode,
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) => {
    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();
    const { requiredScope } = useSelector((state) => state.login);
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const DEFAULT_TPM_PAYLOAD = {
        name: resolveWholesalerProfileName(null, 'Teams Phone Mobile'),
        serviceType: 'TeamsPhoneMobile',
        callingProfiles: [],
        operatorProfiles: [],
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        visibleScope: level === 'partner' ? 20 : 60,
    };

    const [modalState, setModalState] = useState(DEFAULT_TPM_PAYLOAD);

    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    useEffect(() => {
        if (
            mode !== 'EDIT' &&
            level !== 'partner' &&
            currentWholesalerData?.name
        ) {
            handleChangeState({
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    'Teams Phone Mobile',
                ),
            });
        } else if (
            mode !== 'EDIT' &&
            level === 'partner' &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - Teams Phone Mobile Partner Profile`,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, companiesAndPartners, currentWholesalerData?.name, level]);
    const { validatefn } = useFieldValidation(modalState, validationSchemaTPM);

    const scope =
        level === 'partner'
            ? `Partner/${data?.partnerID || partnerID}`
            : `Wholesaler/${data?.wholesalerID || wholesalerID}`;

    const isTPMDataQueryEnabled = mode === 'EDIT' && Boolean(data);

    const { isLoading: loadingTPMData } = useQuery({
        queryKey: ['tpmData', mode, data?.id, scope],
        queryFn: async () => {
            const tpmRes = await axios.get(
                `/serviceprofile/teamsphonemobile/${scope}`,
            );
            return tpmRes.data;
        },
        enabled: isTPMDataQueryEnabled,
        onSuccess: (tpmData) => {
            setModalState({
                ...tpmData,
                callingProfiles:
                    tpmData.callingProfiles?.map(multiSelectDataMapper) || [],
                operatorProfiles:
                    tpmData.operatorProfiles?.map(multiSelectDataMapper) || [],
            });
        },
        onError: (e) => {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred loading service profile data.',
            );
        },
    });

    const { data: operatorsEnum = [], isLoading: loadingOperators } = useQuery({
        queryKey: ['tpmOperatorProfiles', scope],
        queryFn: async () => {
            const opResult = await axios.get(
                `/serviceprofiles/teamsphonemobile/${scope}/operatorprofiles/available`,
            );
            return opResult.data.map(multiSelectDataMapper);
        },
        onError: (e) => {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred loading operator profiles.',
            );
        },
    });

    const operatorProfileIds = (modalState.operatorProfiles || [])
        .map((op) => op?.value ?? op?.id ?? op)
        .filter(Boolean);

    const isCallingProfilesQueryEnabled = operatorProfileIds.length > 0;

    const { data: callingProfiles = [], isLoading: loadingCallingProfiles } =
        useQuery({
            queryKey: ['tpmCallingProfiles', scope, operatorProfileIds],
            queryFn: async () => {
                const res = await axios.get(
                    `/ServiceProfiles/teamsphonemobile/${scope}/CallingProfiles/Available?OperatorProfileIDs=${query(
                        modalState.operatorProfiles,
                    )}`,
                );
                return res.data.map(multiSelectDataMapper);
            },
            enabled: isCallingProfilesQueryEnabled,
            onError: (e) => {
                setError(
                    e?.response?.data?.message ||
                        e?.message ||
                        'An error occurred loading calling profiles.',
                );
            },
        });

    if (
        isLoading ||
        loadingOperators ||
        (isTPMDataQueryEnabled && loadingTPMData) ||
        (isCallingProfilesQueryEnabled && loadingCallingProfiles)
    ) {
        return <LoadingIndicator />;
    }

    // Formik initial values (always use modalState, which is updated after async fetch)
    const initialValues = {
        name: modalState?.name || '',
        serviceType: 'TeamsPhoneMobile',
        callingProfiles: modalState?.callingProfiles || [],
        operatorProfiles: modalState?.operatorProfiles || [],
        partnerID:
            level === 'partner'
                ? (modalState?.partnerID ?? partnerID)
                : undefined,
        wholesalerID:
            level === 'partner'
                ? undefined
                : (modalState?.wholesalerID ?? wholesalerID),
        visibleScope:
            modalState?.visibleScope ?? (level === 'partner' ? 20 : 60),
    };

    // Formik submit handler
    const finalFormSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);
        setSubmitted(true);
        try {
            if (!validatefn(values)) {
                setError({
                    response: {
                        data: {
                            message:
                                'There are some validation errors in the form. Please check and try again.',
                        },
                    },
                });
                actions.setSubmitting(false);
                setIsLoading(false);
                return;
            }
            if (mode === 'EDIT') {
                await updateServiceProfiles({
                    type: 'TeamsPhoneMobile',
                    payload: modalState,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            } else {
                await createServiceProfile({
                    type: 'TeamsPhoneMobile',
                    payload: values,
                    level,
                    scope: requiredScope,
                });
                handleBackButton();
            }
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    e?.message ||
                    'An error occurred. Please try again later.',
            );
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`${toSentenceCase(mode)} ${modalState.name || data?.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                enableReinitialize={true}>
                {({ errors, touched }) =>
                    [
                        <ServiceProfileVisibleScope
                            key="tpmServiceProfileVisibleScope"
                            name="visibleScope"
                            options={visibleScopeOptions}
                            darkMode={darkMode}
                            minWidth="420px"
                            maxWidth="520px"
                            width="100%"
                            errors={errors}
                            touched={touched}
                            onChange={(e) => {
                                setModalState((prev) => ({
                                    ...prev,
                                    visibleScope: e.value,
                                }));
                            }}
                        />,
                        <TPMOperatorsField
                            key="operators"
                            value={modalState.operatorProfiles}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    operatorProfiles: val,
                                }))
                            }
                            options={operatorsEnum}
                            darkMode={darkMode}
                            error={
                                !modalState.operatorProfiles ||
                                modalState.operatorProfiles.length === 0
                                    ? 'At least one operator is required'
                                    : ''
                            }
                            showError={submitted}
                            errors={errors}
                            touched={touched}
                            disableStackHeight={true}
                        />,
                        <TPMCallingProfilesField
                            key="callingProfiles"
                            value={modalState.callingProfiles}
                            onChange={(val) =>
                                setModalState((prev) => ({
                                    ...prev,
                                    callingProfiles: val,
                                }))
                            }
                            options={callingProfiles}
                            darkMode={darkMode}
                            error={
                                !modalState.callingProfiles ||
                                modalState.callingProfiles.length === 0
                                    ? 'At least one calling profile is required'
                                    : ''
                            }
                            showError={submitted}
                            errors={errors}
                            touched={touched}
                            disableStackHeight={true}
                        />,
                        error ? (
                            <Box paddingBottom="0.5rem" key="error-box-tpm">
                                <Alert
                                    key="error"
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ) : null,
                    ].filter(Boolean)
                }
            </FormBase>
        </>
    );
};

export default TeamsPhoneMobile;
